/**
 * ThemeProvider — Fetches theme configuration from the public CMS API
 * and applies CSS custom properties to the document root.
 * Supports: colors, typography, layout, and animation settings.
 */
import { useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ThemeConfig {
  primary_color: string
  secondary_color: string
  accent_color: string
  text_color: string
  background_color: string
  font_family: string
  border_radius?: 'small' | 'medium' | 'large'
  button_style?: 'solid' | 'outline' | 'ghost'
  enable_animations?: boolean
  animation_speed?: 'slow' | 'normal' | 'fast'
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_THEME: ThemeConfig = {
  primary_color: '#3d868b',
  secondary_color: '#10B981',
  accent_color: '#F59E0B',
  text_color: '#1F2937',
  background_color: '#FFFFFF',
  font_family: 'Outfit',
  border_radius: 'medium',
  button_style: 'solid',
  enable_animations: true,
  animation_speed: 'normal',
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTheme() {
  return useQuery<ThemeConfig>({
    queryKey: ['cms-theme'],
    queryFn: async () => {
      const { data } = await api.get<{ data: ThemeConfig }>('/cms/theme')
      return data.data
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
  })
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 61, g: 134, b: 139 }
}

function mix(color: { r: number; g: number; b: number }, mixColor: { r: number; g: number; b: number }, weight: number) {
  return {
    r: Math.round(color.r * (1 - weight) + mixColor.r * weight),
    g: Math.round(color.g * (1 - weight) + mixColor.g * weight),
    b: Math.round(color.b * (1 - weight) + mixColor.b * weight),
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { data: theme } = useTheme()

  const config = { ...DEFAULT_THEME, ...theme }

  useEffect(() => {
    const root = document.documentElement.style
    
    // Colors
    root.setProperty('--color-primary', config.primary_color)
    root.setProperty('--color-secondary', config.secondary_color || DEFAULT_THEME.secondary_color)
    root.setProperty('--color-accent', config.accent_color || DEFAULT_THEME.accent_color)
    root.setProperty('--color-text', config.text_color || DEFAULT_THEME.text_color)
    root.setProperty('--color-bg', config.background_color || DEFAULT_THEME.background_color)
    
    // Typography
    root.setProperty('--font-family', config.font_family)
    
    // Layout
    const borderRadiusMap = {
      small: '4px',
      medium: '8px',
      large: '16px',
    }
    root.setProperty('--border-radius', borderRadiusMap[config.border_radius || 'medium'])
    
    // Button Style
    root.setProperty('--button-style', config.button_style || 'solid')
    
    // Animations
    root.setProperty('--animations-enabled', config.enable_animations ? '1' : '0')
    const speedMap = { slow: '500ms', normal: '300ms', fast: '150ms' }
    root.setProperty('--animation-speed', speedMap[config.animation_speed || 'normal'])

    // Generate color shades for primary color
    const rgb = hexToRgb(config.primary_color)
    const white = { r: 255, g: 255, b: 255 }
    const black = { r: 0, g: 0, b: 0 }

    const shades = {
      50: mix(rgb, white, 0.92),
      100: mix(rgb, white, 0.80),
      200: mix(rgb, white, 0.60),
      300: mix(rgb, white, 0.40),
      400: mix(rgb, white, 0.20),
      500: rgb,
      600: mix(rgb, black, 0.15),
      700: mix(rgb, black, 0.30),
      800: mix(rgb, black, 0.45),
      900: mix(rgb, black, 0.60),
      950: mix(rgb, black, 0.75),
    }

    Object.entries(shades).forEach(([shade, color]) => {
      root.setProperty(`--primary-${shade}`, `${color.r} ${color.g} ${color.b}`)
    })
  }, [config])

  useEffect(() => {
    if (!config.font_family) return

    const fontId = 'dynamic-google-font'
    let linkElement = document.getElementById(fontId) as HTMLLinkElement | null
    const formattedFont = config.font_family.replace(/\s+/g, '+')
    const fontUrl = `https://fonts.googleapis.com/css2?family=${formattedFont}:wght@300;400;500;600;700;800&display=swap`

    if (!linkElement) {
      linkElement = document.createElement('link')
      linkElement.id = fontId
      linkElement.rel = 'stylesheet'
      document.head.appendChild(linkElement)
    }
    linkElement.href = fontUrl
  }, [config.font_family])

  return <>{children}</>
}
